// Los textos del comparativo de ofertas y de los precios ofertados.
//
// Vinieron de `construction` cuando el comparativo pasó a Compras (0.12.0): la
// función es de quien compra, aunque solo se encienda con Obras instalado. Van
// aparte del catálogo grande por lo mismo que la analítica: son un bloque que
// se lee entero, y meterlos en medio de las ochocientas líneas de cada idioma
// los haría imposibles de revisar juntos.
//
// `ui` son las palabras sueltas que la pantalla compartía con otras de Obras
// («Acciones», «Empresa», «Precio»): copiadas aquí, porque un módulo no lee las
// claves de otro y en una instancia sin Obras no existirían.

const tenders = {
  es: {
    // La portada de la ayuda del módulo (help/es.md).
    help: {
      title: 'Compras',
      subtitle: 'Sacar un paquete de obra a precio, comparar las ofertas y adjudicar',
    },
    tenders: {
      title: 'Comparativo de ofertas',
      back: 'A los paquetes',
      pageTitle: 'Comparativo · {code} {name}',
      pageSubtitle: 'Comparativo n.º {number} · {count} línea | Comparativo n.º {number} · {count} líneas',
      print: 'Imprimir PDF',
      template: 'Plantilla para las empresas',
      addOffer: 'Añadir oferta',
      editOffer: 'Editar oferta',
      status: {
        open: 'Licitando',
        awarded: 'Adjudicado',
        cancelled: 'Cerrado sin adjudicar',
      },
      dueOn: 'Ofertas hasta el {date}',
      frozen: 'Congelado el {date}: así se vio al adjudicar',
      target: 'Objetivo',
      offersCount: 'Ofertas',
      bestComplete: 'Mejor oferta completa',
      vsTarget: 'Contra objetivo',
      linesChanged: 'El paquete ha cambiado desde que se abrió el comparativo: sus líneas ya no son las que se pidieron.',
      refreshLines: 'Actualizar líneas',
      linesRefreshed: 'Líneas actualizadas. Los precios de cada empresa se conservan por partida.',
      ruleLabel: 'Las líneas no ofertadas se completan con',
      rule: {
        highest: 'El precio más alto ofertado',
        target: 'El objetivo',
      },
      ruleHelp: {
        highest: 'Homogeneizado: cada línea que una empresa no oferta se completa con el precio más alto que han ofertado las demás (o con el objetivo si nadie la ha ofertado). Es la regla prudente: esa línea la acabarás contratando, y no te va a salir barata.',
        target: 'Homogeneizado: cada línea que una empresa no oferta se completa al precio objetivo de la línea.',
      },
      awardedTo: 'Adjudicado a {supplier}',
      awardedBy: 'Por {who} el {date}',
      wasRecommended: 'era la oferta recomendada',
      wasNotRecommended: 'la recomendada era {supplier}',
      reason: 'Motivo',
      seeSubcontract: 'Ver la subcontrata que se dio de alta',
      orderNote: 'Compromiso apuntado en el paquete. Si compras está instalado, el pedido queda en borrador con estas líneas y estos precios: lo manda quien compra.',
      cancelAward: 'Anular la adjudicación',
      cancelAwardHelp: 'Para cuando la empresa se echa atrás antes de empezar. El comparativo se reabre con sus ofertas, la subcontrata se anula (si no se le ha certificado nada) y el compromiso se retira. La adjudicación anulada queda en el historial.',
      cancelAwardWithdraw: 'La empresa se retira: su oferta deja de competir',
      awardCancelled: 'Adjudicación anulada. El comparativo vuelve a estar abierto.',
      recommended: 'Recomendada: {supplier}',
      recommendedShort: 'Recomendada',
      basis: {
        cheapest_complete: 'Es la oferta completa y vigente más barata, contando las líneas homogeneizadas.',
        cheapest_complete_expired: 'Es la completa más barata, pero todas las completas están caducadas: pide a la empresa que mantenga el precio antes de adjudicar.',
        no_complete: 'Ninguna oferta está completa: es la más barata homogeneizada. Pide precio de lo que falta antes de adjudicar.',
      },
      belowTarget: 'Queda {amount} ({pct}) por debajo del objetivo.',
      aboveTarget: 'Queda {amount} ({pct}) por encima del objetivo.',
      runnerUp: 'La siguiente es {supplier}, {amount} más cara.',
      cheaperIncomplete: '{supplier} oferta {offered}, pero deja {count} línea sin precio: homogeneizada sale {homogenized}. | {supplier} oferta {offered}, pero deja {count} líneas sin precio: homogeneizada sale {homogenized}.',
      cheaperExpired: '{supplier} sería más barata ({amount}) pero su oferta ha caducado: pídele que la renueve.',
      awardRecommended: 'Adjudicar a la recomendada',
      emptyTitle: 'Sin ofertas todavía',
      emptyHelp: 'Descarga la plantilla, mándala a tres o cuatro empresas y mete aquí sus precios: tecleados o leyendo su Excel.',
      offerStatus: {
        received: 'Recibida',
        awarded: 'Adjudicada',
        not_awarded: 'No adjudicada',
        withdrawn: 'Retirada',
      },
      expired: 'Caducada',
      missingCount: '{count} línea sin precio | {count} líneas sin precio',
      offered: 'Ofertado',
      homogenized: 'Homogeneizado',
      bestLines: 'Líneas con el mejor precio',
      reference: 'N.º de oferta',
      validUntil: 'Válida hasta',
      executionDays: 'Plazo (días)',
      days: '{count} día | {count} días',
      paymentTerms: 'Forma de pago',
      paymentTermsPlaceholder: 'Ej.: 60 días fecha factura',
      retentionPct: 'Retención (%)',
      discountPct: 'Descuento global (%)',
      discountHelp: 'Sobre lo que oferta, no sobre las líneas que se completan.',
      discountShort: 'dto. {pct} %',
      totalDeclared: 'Total que escribe la empresa',
      totalDeclaredHelp: 'El de su oferta. Si no cuadra con sus precios, se avisa.',
      declaredMismatch: 'Su oferta dice {declared} y sus precios suman {computed}: revisa qué se ha tecleado mal.',
      strongCount: '{count} precio se desvía mucho de la media: revisa unidad y medición. | {count} precios se desvían mucho de la media: revisa unidades y mediciones.',
      exclusions: 'No incluye',
      exclusionsHelp: 'Lo que la oferta deja fuera. Una por línea, con Intro.',
      exclusionsPlaceholder: 'Ej.: reposición de pavimento',
      notes: 'Notas',
      documents: 'Sin papeles | 1 papel | {count} papeles',
      documentsTitle: 'Papeles de la oferta de {supplier}',
      withdrawOffer: 'Retirar',
      restoreOffer: 'Volver a tenerla en cuenta',
      removeOffer: 'Quitar',
      withdrawHelp: 'La empresa se echa atrás. La oferta se queda en el comparativo, pero deja de competir y de marcar mejores precios.',
      restoreHelp: 'La oferta vuelve a competir con las demás.',
      removeOfferTitle: '¿Quitar la oferta?',
      removeOfferHelp: 'Se borra la oferta de {supplier} con sus precios y sus papeles. Si la empresa se ha echado atrás, mejor «Retirar»: así consta.',
      award: 'Adjudicar',
      awardTitle: 'Adjudicar el paquete',
      awardLead: 'Adjudicar a {supplier} por {amount}.',
      awardSubcontractHelp: 'Se da de alta la subcontrata enlazada a este paquete, con el importe de la oferta, un {retention} % de retención y {days} días de plazo. Su alcance se lee del paquete.',
      awardOrderHelp: 'Se apunta en el paquete un compromiso de {amount}, y compras deja el pedido en borrador con estas líneas. Cada pedido que se confirme a esta empresa en este paquete descuenta del compromiso.',
      awardIncomplete: 'Esta oferta deja {count} línea sin precio, que vale {amount} al objetivo: habrá que contratarla aparte. | Esta oferta deja {count} líneas sin precio, que valen {amount} al objetivo: habrá que contratarlas aparte.',
      signedLabel: 'Firma',
      signedHelp: 'Dejarla ya firmada (compromete su importe desde hoy)',
      reasonRequired: 'Motivo (obligatorio: la recomendada es {supplier})',
      reasonOptional: 'Motivo (opcional)',
      awarded: 'Paquete adjudicado. El comparativo queda congelado.',
      tableTitle: 'Tabla comparativa',
      viewLabel: 'Ver',
      view: {
        table: 'Tabla',
        lines: 'Por línea',
        offers: 'Por oferta',
      },
      line: 'Línea',
      unitPrice: 'Precio',
      amount: 'Importe',
      pastPrices: 'Precios de otras obras',
      notOffered: 'No ofertada',
      filledWith: 'se completa con {amount}',
      lastShort: 'últ. {price}',
      lastLong: 'La última vez ofertó {price} ({work}, {date})',
      notOfferedLines: 'Líneas no ofertadas',
      targetShort: 'objetivo {price} · {amount}',
      best: 'Mejor',
      pickOffer: 'Oferta',
      historyTitle: 'Historial del comparativo',
      cancelTender: 'Cerrar sin adjudicar',
      cancelTenderHelp: 'El paquete vuelve a «Abierto». Los precios se quedan en el histórico: fueron ofertas de verdad.',
      offerDate: 'Fecha de la oferta',
      pricesTitle: 'Precios por línea',
      pricesHelp: 'En blanco es «no la oferta», y así se ve en la tabla. Un 0 es un precio: la línea va incluida en otra.',
      importSheet: 'Leer su Excel',
      reading: 'Leyendo…',
      sheetRead: '{file} · hoja «{sheet}»: {priced} de {lines} líneas con precio. Revisa y guarda.',
      sheetMissing: '{count} línea no aparece en su hoja: queda sin ofertar. | {count} líneas no aparecen en su hoja: quedan sin ofertar.',
      sheetQuantity: '{code}: ha ofertado {offered} {unit} donde se pedían {asked}. Su total no es comparable hasta que se aclare.',
      sheetUnmatched: 'Fila {row} sin casar con ninguna línea: {text}',
      draftTotal: 'Ofertado: {amount} · sin líneas sin precio | Ofertado: {amount} · {count} línea sin precio | Ofertado: {amount} · {count} líneas sin precio',
      offerSaved: 'Oferta guardada',
      history: {
        opened: 'Comparativo abierto con {count} líneas',
        edited: 'Cambiada la regla o la fecha',
        lines_refreshed: 'Líneas actualizadas desde el paquete ({count})',
        cancelled: 'Cerrado sin adjudicar: {comment}',
        offer_added: 'Oferta de {supplier}',
        offer_revised: 'Revisada la oferta de {supplier}',
        offer_withdrawn: '{supplier} retira su oferta {comment}',
        offer_restored: 'La oferta de {supplier} vuelve a competir',
        offer_removed: 'Quitada la oferta de {supplier}',
        awarded: 'Adjudicado a {supplier} por {amount} {comment}',
        award_cancelled: 'Anulada la adjudicación a {supplier}: {comment}',
      },
      templateLabels: {
        code: 'Código',
        description: 'Concepto',
        unit: 'Ud',
        quantity: 'Cantidad',
        price: 'Precio unitario',
        amount: 'Importe',
        total: 'TOTAL',
      },
      pdf: {
        title: 'Comparativo de ofertas',
        work: 'Obra',
        package: 'Paquete',
        date: 'Fecha',
        code: 'Código',
        description: 'Concepto',
        unit: 'Ud',
        quantity: 'Cantidad',
        target: 'Objetivo',
        unitPrice: 'Precio',
        amount: 'Importe',
        notOffered: 'no ofertada',
        offered: 'Ofertado',
        homogenized: 'Homogeneizado',
        vsTarget: 'Contra objetivo',
        missing: 'Líneas no ofertadas',
        ruleHighest: 'Las líneas no ofertadas se completan con el precio más alto ofertado por las demás empresas (o con el objetivo si nadie las ofertó).',
        ruleTarget: 'Las líneas no ofertadas se completan al precio objetivo.',
        recommended: 'Recomendada',
        awardedTo: 'Adjudicado a',
        reason: 'Motivo',
        best: 'Mejor precio',
        frozen: 'Registro congelado de la adjudicación',
        validUntil: 'válida hasta',
        expired: 'caducada',
        withdrawn: 'retirada',
        exclusions: 'No incluye',
        discount: 'Descuento',
      },
      rowOpen: 'licitando, sin ofertas | licitando, 1 oferta | licitando, {count} ofertas',
      rowAwarded: 'adjudicado a {supplier}',
      panelAwarded: 'Adjudicado a {supplier} por {amount}. El comparativo queda como registro.',
      panelOpen: 'Licitando, todavía sin ofertas. | Licitando, con 1 oferta. | Licitando, con {count} ofertas.',
      panelNone: 'Pide precio a varias empresas sobre lo que reclama el paquete y compáralas contra su objetivo.',
      openComparison: 'Abrir el comparativo',
      start: 'Sacar a precio',
      fromTender: 'Adjudicada desde un comparativo de ofertas: ver las demás ofertas y el motivo',
      menu: 'Comparativos',
      seeWork: 'Ver la obra',
      seePurchase: 'Ver el pedido en borrador',
      seeSupplier: 'Ficha del proveedor',
      list: {
        title: 'Comparativos de ofertas',
        subtitle: 'Las ofertas de cada paquete de obra, una al lado de la otra, y a quién se adjudicó',
        all: 'Todos',
        status: 'Estado',
        work: 'Obra',
        package: 'Paquete',
        offers: 'Ofertas',
        supplier: 'Adjudicado a',
        amount: 'Importe',
        created: 'Abierto',
        count: 'Comparativos',
        search: 'Buscar por paquete, obra o empresa',
        emptyTitle: 'No hay comparativos',
        emptyHelp: 'Se abren desde el paquete de contratación de una obra, con «Sacar a precio».',
      },
      opening: {
        title: 'Abriendo el comparativo',
        working: 'Buscando el comparativo del paquete…',
        noPackage: 'Falta el paquete: este comparativo se abre desde el paquete de contratación de una obra.',
      },
      supplierPanel: {
        awards: 'Adjudicaciones',
        offers: 'Ofertas en comparativos',
      },
      packageType: {
        supply: 'Suministro',
        subcontract: 'Subcontrata',
        hire: 'Alquiler',
        service: 'Servicio',
        own_means: 'Medios propios',
      },
      ui: {
        more: 'Acciones',
        price: 'Precio',
        unit: 'Ud',
        concept: 'Concepto',
        showAll: 'Ver todas',
        pager: {
          previous: 'Anterior',
          next: 'Siguiente',
          of: 'Página {page} de {pages}',
        },
        addDocument: 'Subir un documento',
        company: 'Empresa',
        companyPlaceholder: 'Buscar el proveedor',
        dropDocument: 'Quitar el documento',
        dropDocumentConfirm: '«{name}» deja de estar en el contrato.',
        noDocuments: 'Sin papeles. Sube aquí el contrato firmado, las adendas y los certificados.',
        start: 'Inicio',
        uploading: 'Subiendo…',
      },
    },
    priceHistory: {
      title: 'Precios ofertados antes para esta unidad',
      empty: 'Todavía no hay ofertas guardadas para esta unidad de obra. Cada precio que entre en un comparativo aparecerá aquí.',
      min: 'Mínimo',
      median: 'Mediana',
      max: 'Máximo',
      lastAwarded: 'Último adjudicado',
      againstTarget: 'Precio objetivo de esta partida: {target}. Mediana de lo ofertado: {median}.',
      awarded: 'Adjudicado',
      howMatched: 'Se buscan por código de base de precios o por concepto parecido, con la misma unidad y los mismos números (diámetro, espesor, resistencia).',
    },
    tenderPrices: {
      title: 'Precios ofertados',
      subtitle: 'Lo que las empresas han ofertado y lo que se ha contratado, en todas las obras',
      count: 'Precios',
      search: 'Concepto o código…',
      unitPlaceholder: 'm3, m2, kg…',
      supplier: 'Empresa',
      which: 'Qué precios',
      allPrices: 'Ofertados y contratados',
      onlyAwarded: 'Solo contratados',
      mixedUnits: 'Hay precios de varias unidades. Escribe una unidad para ver el precio medio, el mínimo y el último.',
      mean: 'Precio medio',
      min: 'Mínimo',
      max: 'Máximo',
      last: 'Último',
      lastAwarded: 'Último contratado',
      fromWhere: '{supplier} · {work} · {date}',
      awarded: 'Contratado',
      origin: 'Obra y comparativo',
      date: 'Fecha',
      emptyTitle: 'Ningún precio con estos filtros',
      emptyHelp: 'Cada oferta que se mete en un comparativo deja aquí sus precios, línea a línea.',
      onlySupplier: 'Solo los precios de {name}',
      oneSupplier: 'este proveedor',
      menu: 'Precios ofertados',
    },
    related: {
      tenders: 'Comparativos',
      itemPrices: 'Precios anteriores',
      supplierTenders: 'Comparativos',
      purchaseTender: 'Comparativo',
    },
  },
  ca: {
    // La portada de la ayuda del módulo (help/ca.md).
    help: {
      title: 'Compres',
      subtitle: 'Treure un paquet d’obra a preu, comparar les ofertes i adjudicar',
    },
    tenders: {
      title: 'Comparatiu d’ofertes',
      back: 'Als paquets',
      pageTitle: 'Comparatiu · {code} {name}',
      pageSubtitle: 'Comparatiu núm. {number} · {count} línia | Comparatiu núm. {number} · {count} línies',
      print: 'Imprimir PDF',
      template: 'Plantilla per a les empreses',
      addOffer: 'Afegir oferta',
      editOffer: 'Editar oferta',
      status: {
        open: 'Licitant',
        awarded: 'Adjudicat',
        cancelled: 'Tancat sense adjudicar',
      },
      dueOn: 'Ofertes fins al {date}',
      frozen: 'Congelat el {date}: així es va veure en adjudicar',
      target: 'Objectiu',
      offersCount: 'Ofertes',
      bestComplete: 'Millor oferta completa',
      vsTarget: 'Contra objectiu',
      linesChanged: 'El paquet ha canviat des que es va obrir el comparatiu: les seves línies ja no són les que es van demanar.',
      refreshLines: 'Actualitzar línies',
      linesRefreshed: 'Línies actualitzades. Els preus de cada empresa es conserven per partida.',
      ruleLabel: 'Les línies no ofertades es completen amb',
      rule: {
        highest: 'El preu més alt ofertat',
        target: 'L’objectiu',
      },
      ruleHelp: {
        highest: 'Homogeneïtzat: cada línia que una empresa no oferta es completa amb el preu més alt que han ofertat les altres (o amb l’objectiu si ningú no l’ha ofertada). És la regla prudent: aquesta línia l’acabaràs contractant, i no et sortirà barata.',
        target: 'Homogeneïtzat: cada línia que una empresa no oferta es completa al preu objectiu de la línia.',
      },
      awardedTo: 'Adjudicat a {supplier}',
      awardedBy: 'Per {who} el {date}',
      wasRecommended: 'era l’oferta recomanada',
      wasNotRecommended: 'la recomanada era {supplier}',
      reason: 'Motiu',
      seeSubcontract: 'Veure la subcontracta que es va donar d’alta',
      orderNote: 'Compromís apuntat al paquet. Si compres està instal·lat, la comanda queda en esborrany amb aquestes línies i aquests preus: l’envia qui compra.',
      cancelAward: 'Anul·lar l’adjudicació',
      cancelAwardHelp: 'Per quan l’empresa es fa enrere abans de començar. El comparatiu es reobre amb les seves ofertes, la subcontracta s’anul·la (si no se li ha certificat res) i el compromís es retira. L’adjudicació anul·lada queda a l’historial.',
      cancelAwardWithdraw: 'L’empresa es retira: la seva oferta deixa de competir',
      awardCancelled: 'Adjudicació anul·lada. El comparatiu torna a estar obert.',
      recommended: 'Recomanada: {supplier}',
      recommendedShort: 'Recomanada',
      basis: {
        cheapest_complete: 'És l’oferta completa i vigent més barata, comptant les línies homogeneïtzades.',
        cheapest_complete_expired: 'És la completa més barata, però totes les completes han caducat: demana a l’empresa que mantingui el preu abans d’adjudicar.',
        no_complete: 'Cap oferta no és completa: és la més barata homogeneïtzada. Demana preu del que falta abans d’adjudicar.',
      },
      belowTarget: 'Queda {amount} ({pct}) per sota de l’objectiu.',
      aboveTarget: 'Queda {amount} ({pct}) per sobre de l’objectiu.',
      runnerUp: 'La següent és {supplier}, {amount} més cara.',
      cheaperIncomplete: '{supplier} oferta {offered}, però deixa {count} línia sense preu: homogeneïtzada surt {homogenized}. | {supplier} oferta {offered}, però deixa {count} línies sense preu: homogeneïtzada surt {homogenized}.',
      cheaperExpired: '{supplier} seria més barata ({amount}) però la seva oferta ha caducat: demana-li que la renovi.',
      awardRecommended: 'Adjudicar a la recomanada',
      emptyTitle: 'Encara sense ofertes',
      emptyHelp: 'Descarrega la plantilla, envia-la a tres o quatre empreses i posa aquí els seus preus: teclejats o llegint el seu Excel.',
      offerStatus: {
        received: 'Rebuda',
        awarded: 'Adjudicada',
        not_awarded: 'No adjudicada',
        withdrawn: 'Retirada',
      },
      expired: 'Caducada',
      missingCount: '{count} línia sense preu | {count} línies sense preu',
      offered: 'Ofertat',
      homogenized: 'Homogeneïtzat',
      bestLines: 'Línies amb el millor preu',
      reference: 'Núm. d’oferta',
      validUntil: 'Vàlida fins a',
      executionDays: 'Termini (dies)',
      days: '{count} dia | {count} dies',
      paymentTerms: 'Forma de pagament',
      paymentTermsPlaceholder: 'Ex.: 60 dies data factura',
      retentionPct: 'Retenció (%)',
      discountPct: 'Descompte global (%)',
      discountHelp: 'Sobre el que oferta, no sobre les línies que es completen.',
      discountShort: 'dte. {pct} %',
      totalDeclared: 'Total que escriu l’empresa',
      totalDeclaredHelp: 'El de la seva oferta. Si no quadra amb els seus preus, s’avisa.',
      declaredMismatch: 'La seva oferta diu {declared} i els seus preus sumen {computed}: revisa què s’ha teclejat malament.',
      strongCount: '{count} preu es desvia molt de la mitjana: revisa unitat i amidament. | {count} preus es desvien molt de la mitjana: revisa unitats i amidaments.',
      exclusions: 'No inclou',
      exclusionsHelp: 'El que l’oferta deixa fora. Una per línia, amb Intro.',
      exclusionsPlaceholder: 'Ex.: reposició de paviment',
      notes: 'Notes',
      documents: 'Sense papers | 1 paper | {count} papers',
      documentsTitle: 'Papers de l’oferta de {supplier}',
      withdrawOffer: 'Retirar',
      restoreOffer: 'Tornar-la a tenir en compte',
      removeOffer: 'Treure',
      withdrawHelp: 'L’empresa es fa enrere. L’oferta es queda al comparatiu, però deixa de competir i de marcar millors preus.',
      restoreHelp: 'L’oferta torna a competir amb les altres.',
      removeOfferTitle: 'Treure l’oferta?',
      removeOfferHelp: 'S’esborra l’oferta de {supplier} amb els seus preus i els seus papers. Si l’empresa s’ha fet enrere, millor «Retirar»: així consta.',
      award: 'Adjudicar',
      awardTitle: 'Adjudicar el paquet',
      awardLead: 'Adjudicar a {supplier} per {amount}.',
      awardSubcontractHelp: 'Es dona d’alta la subcontracta enllaçada a aquest paquet, amb l’import de l’oferta, un {retention} % de retenció i {days} dies de termini. El seu abast es llegeix del paquet.',
      awardOrderHelp: 'S’apunta al paquet un compromís de {amount}, i compres deixa la comanda en esborrany amb aquestes línies. Cada comanda que es confirmi a aquesta empresa en aquest paquet descompta del compromís.',
      awardIncomplete: 'Aquesta oferta deixa {count} línia sense preu, que val {amount} a l’objectiu: caldrà contractar-la a part. | Aquesta oferta deixa {count} línies sense preu, que valen {amount} a l’objectiu: caldrà contractar-les a part.',
      signedLabel: 'Signatura',
      signedHelp: 'Deixar-la ja signada (compromet el seu import des d’avui)',
      reasonRequired: 'Motiu (obligatori: la recomanada és {supplier})',
      reasonOptional: 'Motiu (opcional)',
      awarded: 'Paquet adjudicat. El comparatiu queda congelat.',
      tableTitle: 'Taula comparativa',
      viewLabel: 'Veure',
      view: {
        table: 'Taula',
        lines: 'Per línia',
        offers: 'Per oferta',
      },
      line: 'Línia',
      unitPrice: 'Preu',
      amount: 'Import',
      pastPrices: 'Preus d’altres obres',
      notOffered: 'No ofertada',
      filledWith: 'es completa amb {amount}',
      lastShort: 'últ. {price}',
      lastLong: 'L’última vegada va ofertar {price} ({work}, {date})',
      notOfferedLines: 'Línies no ofertades',
      targetShort: 'objectiu {price} · {amount}',
      best: 'Millor',
      pickOffer: 'Oferta',
      historyTitle: 'Historial del comparatiu',
      cancelTender: 'Tancar sense adjudicar',
      cancelTenderHelp: 'El paquet torna a «Obert». Els preus es queden a l’històric: van ser ofertes de veritat.',
      offerDate: 'Data de l’oferta',
      pricesTitle: 'Preus per línia',
      pricesHelp: 'En blanc és «no l’oferta», i així es veu a la taula. Un 0 és un preu: la línia va inclosa en una altra.',
      importSheet: 'Llegir el seu Excel',
      reading: 'Llegint…',
      sheetRead: '{file} · full «{sheet}»: {priced} de {lines} línies amb preu. Revisa i desa.',
      sheetMissing: '{count} línia no apareix al seu full: queda sense ofertar. | {count} línies no apareixen al seu full: queden sense ofertar.',
      sheetQuantity: '{code}: ha ofertat {offered} {unit} on se’n demanaven {asked}. El seu total no és comparable fins que s’aclareixi.',
      sheetUnmatched: 'Fila {row} sense casar amb cap línia: {text}',
      draftTotal: 'Ofertat: {amount} · cap línia sense preu | Ofertat: {amount} · {count} línia sense preu | Ofertat: {amount} · {count} línies sense preu',
      offerSaved: 'Oferta desada',
      history: {
        opened: 'Comparatiu obert amb {count} línies',
        edited: 'Canviada la regla o la data',
        lines_refreshed: 'Línies actualitzades des del paquet ({count})',
        cancelled: 'Tancat sense adjudicar: {comment}',
        offer_added: 'Oferta de {supplier}',
        offer_revised: 'Revisada l’oferta de {supplier}',
        offer_withdrawn: '{supplier} retira la seva oferta {comment}',
        offer_restored: 'L’oferta de {supplier} torna a competir',
        offer_removed: 'Treta l’oferta de {supplier}',
        awarded: 'Adjudicat a {supplier} per {amount} {comment}',
        award_cancelled: 'Anul·lada l’adjudicació a {supplier}: {comment}',
      },
      templateLabels: {
        code: 'Codi',
        description: 'Concepte',
        unit: 'Ut',
        quantity: 'Quantitat',
        price: 'Preu unitari',
        amount: 'Import',
        total: 'TOTAL',
      },
      pdf: {
        title: 'Comparatiu d’ofertes',
        work: 'Obra',
        package: 'Paquet',
        date: 'Data',
        code: 'Codi',
        description: 'Concepte',
        unit: 'Ut',
        quantity: 'Quantitat',
        target: 'Objectiu',
        unitPrice: 'Preu',
        amount: 'Import',
        notOffered: 'no ofertada',
        offered: 'Ofertat',
        homogenized: 'Homogeneïtzat',
        vsTarget: 'Contra objectiu',
        missing: 'Línies no ofertades',
        ruleHighest: 'Les línies no ofertades es completen amb el preu més alt ofertat per les altres empreses (o amb l’objectiu si ningú no les va ofertar).',
        ruleTarget: 'Les línies no ofertades es completen al preu objectiu.',
        recommended: 'Recomanada',
        awardedTo: 'Adjudicat a',
        reason: 'Motiu',
        best: 'Millor preu',
        frozen: 'Registre congelat de l’adjudicació',
        validUntil: 'vàlida fins a',
        expired: 'caducada',
        withdrawn: 'retirada',
        exclusions: 'No inclou',
        discount: 'Descompte',
      },
      rowOpen: 'licitant, sense ofertes | licitant, 1 oferta | licitant, {count} ofertes',
      rowAwarded: 'adjudicat a {supplier}',
      panelAwarded: 'Adjudicat a {supplier} per {amount}. El comparatiu queda com a registre.',
      panelOpen: 'Licitant, encara sense ofertes. | Licitant, amb 1 oferta. | Licitant, amb {count} ofertes.',
      panelNone: 'Demana preu a diverses empreses sobre el que reclama el paquet i compara-les amb el seu objectiu.',
      openComparison: 'Obrir el comparatiu',
      start: 'Treure a preu',
      fromTender: 'Adjudicada des d’un comparatiu d’ofertes: veure les altres ofertes i el motiu',
      menu: 'Comparatius',
      seeWork: 'Veure l’obra',
      seePurchase: 'Veure la comanda en esborrany',
      seeSupplier: 'Fitxa del proveïdor',
      list: {
        title: 'Comparatius d’ofertes',
        subtitle: 'Les ofertes de cada paquet d’obra, l’una al costat de l’altra, i a qui es va adjudicar',
        all: 'Tots',
        status: 'Estat',
        work: 'Obra',
        package: 'Paquet',
        offers: 'Ofertes',
        supplier: 'Adjudicat a',
        amount: 'Import',
        created: 'Obert',
        count: 'Comparatius',
        search: 'Cerca per paquet, obra o empresa',
        emptyTitle: 'No hi ha comparatius',
        emptyHelp: 'S’obren des del paquet de contractació d’una obra, amb «Treure a preu».',
      },
      opening: {
        title: 'Obrint el comparatiu',
        working: 'Cercant el comparatiu del paquet…',
        noPackage: 'Falta el paquet: aquest comparatiu s’obre des del paquet de contractació d’una obra.',
      },
      supplierPanel: {
        awards: 'Adjudicacions',
        offers: 'Ofertes en comparatius',
      },
      packageType: {
        supply: 'Subministrament',
        subcontract: 'Subcontracta',
        hire: 'Lloguer',
        service: 'Servei',
        own_means: 'Mitjans propis',
      },
      ui: {
        more: 'Accions',
        price: 'Preu',
        unit: 'Ut',
        concept: 'Concepte',
        showAll: 'Veure-les totes',
        pager: {
          previous: 'Anterior',
          next: 'Següent',
          of: 'Pàgina {page} de {pages}',
        },
        addDocument: 'Pujar un document',
        company: 'Empresa',
        companyPlaceholder: 'Cercar el proveïdor',
        dropDocument: 'Treure el document',
        dropDocumentConfirm: '«{name}» deixa d’estar al contracte.',
        noDocuments: 'Sense papers. Puja aquí el contracte signat, les addendes i els certificats.',
        start: 'Inici',
        uploading: 'Pujant…',
      },
    },
    priceHistory: {
      title: 'Preus ofertats abans per a aquesta unitat',
      empty: 'Encara no hi ha ofertes desades per a aquesta unitat d’obra. Cada preu que entri en un comparatiu apareixerà aquí.',
      min: 'Mínim',
      median: 'Mediana',
      max: 'Màxim',
      lastAwarded: 'Últim adjudicat',
      againstTarget: 'Preu objectiu d’aquesta partida: {target}. Mediana de l’ofertat: {median}.',
      awarded: 'Adjudicat',
      howMatched: 'Es busquen per codi de base de preus o per concepte semblant, amb la mateixa unitat i els mateixos números (diàmetre, gruix, resistència).',
    },
    tenderPrices: {
      title: 'Preus oferts',
      subtitle: 'El que les empreses han ofert i el que s\'ha contractat, a totes les obres',
      count: 'Preus',
      search: 'Concepte o codi…',
      unitPlaceholder: 'm3, m2, kg…',
      supplier: 'Empresa',
      which: 'Quins preus',
      allPrices: 'Oferts i contractats',
      onlyAwarded: 'Només contractats',
      mixedUnits: 'Hi ha preus de diverses unitats. Escriu una unitat per veure el preu mitjà, el mínim i l\'últim.',
      mean: 'Preu mitjà',
      min: 'Mínim',
      max: 'Màxim',
      last: 'Últim',
      lastAwarded: 'Últim contractat',
      fromWhere: '{supplier} · {work} · {date}',
      awarded: 'Contractat',
      origin: 'Obra i comparatiu',
      date: 'Data',
      emptyTitle: 'Cap preu amb aquests filtres',
      emptyHelp: 'Cada oferta que s\'introdueix en un comparatiu deixa aquí els seus preus, línia a línia.',
      onlySupplier: 'Només els preus de {name}',
      oneSupplier: 'aquest proveïdor',
      menu: 'Preus oferts',
    },
    related: {
      tenders: 'Comparatius',
      itemPrices: 'Preus anteriors',
      supplierTenders: 'Comparatius',
      purchaseTender: 'Comparatiu',
    },
  },
  en: {
    // La portada de la ayuda del módulo (help/en.md).
    help: {
      title: 'Purchasing',
      subtitle: 'Put a works package out to tender, compare the offers and award it',
    },
    tenders: {
      title: 'Bid comparison',
      back: 'To the packages',
      pageTitle: 'Bid comparison · {code} {name}',
      pageSubtitle: 'Comparison no. {number} · {count} line | Comparison no. {number} · {count} lines',
      print: 'Print PDF',
      template: 'Template for bidders',
      addOffer: 'Add offer',
      editOffer: 'Edit offer',
      status: {
        open: 'Out to tender',
        awarded: 'Awarded',
        cancelled: 'Closed without award',
      },
      dueOn: 'Offers by {date}',
      frozen: 'Frozen on {date}: as it stood at the award',
      target: 'Target',
      offersCount: 'Offers',
      bestComplete: 'Best complete offer',
      vsTarget: 'Against target',
      linesChanged: 'The package has changed since this comparison was opened: its lines are no longer the ones that were asked for.',
      refreshLines: 'Refresh lines',
      linesRefreshed: 'Lines refreshed. Each bidder keeps its prices per item.',
      ruleLabel: 'Lines not offered are filled with',
      rule: {
        highest: 'The highest price offered',
        target: 'The target',
      },
      ruleHelp: {
        highest: 'Homogenized: every line a bidder does not price is filled with the highest price the others offered (or the target if nobody offered it). It is the prudent rule: you will end up contracting that line, and it will not come cheap.',
        target: 'Homogenized: every line a bidder does not price is filled at the line’s target price.',
      },
      awardedTo: 'Awarded to {supplier}',
      awardedBy: 'By {who} on {date}',
      wasRecommended: 'it was the recommended offer',
      wasNotRecommended: 'the recommended one was {supplier}',
      reason: 'Reason',
      seeSubcontract: 'Open the subcontract that was created',
      orderNote: 'Commitment recorded on the package. If purchasing is installed, the order is left as a draft with these lines and prices: the buyer sends it.',
      cancelAward: 'Undo the award',
      cancelAwardHelp: 'For when the bidder backs out before starting. The comparison reopens with its offers, the subcontract is cancelled (if nothing has been certified to it) and the commitment is released. The undone award stays in the history.',
      cancelAwardWithdraw: 'The bidder withdraws: its offer stops competing',
      awardCancelled: 'Award undone. The comparison is open again.',
      recommended: 'Recommended: {supplier}',
      recommendedShort: 'Recommended',
      basis: {
        cheapest_complete: 'It is the cheapest complete and valid offer, counting homogenized lines.',
        cheapest_complete_expired: 'It is the cheapest complete offer, but every complete offer has expired: ask the bidder to hold its price before awarding.',
        no_complete: 'No offer is complete: this is the cheapest homogenized one. Ask for prices on what is missing before awarding.',
      },
      belowTarget: 'It is {amount} ({pct}) below target.',
      aboveTarget: 'It is {amount} ({pct}) above target.',
      runnerUp: 'Next comes {supplier}, {amount} dearer.',
      cheaperIncomplete: '{supplier} offers {offered} but leaves {count} line unpriced: homogenized it comes to {homogenized}. | {supplier} offers {offered} but leaves {count} lines unpriced: homogenized it comes to {homogenized}.',
      cheaperExpired: '{supplier} would be cheaper ({amount}) but its offer has expired: ask it to renew.',
      awardRecommended: 'Award to the recommended one',
      emptyTitle: 'No offers yet',
      emptyHelp: 'Download the template, send it to three or four bidders and enter their prices here: typed in or read from their spreadsheet.',
      offerStatus: {
        received: 'Received',
        awarded: 'Awarded',
        not_awarded: 'Not awarded',
        withdrawn: 'Withdrawn',
      },
      expired: 'Expired',
      missingCount: '{count} line unpriced | {count} lines unpriced',
      offered: 'Offered',
      homogenized: 'Homogenized',
      bestLines: 'Lines at best price',
      reference: 'Offer no.',
      validUntil: 'Valid until',
      executionDays: 'Duration (days)',
      days: '{count} day | {count} days',
      paymentTerms: 'Payment terms',
      paymentTermsPlaceholder: 'E.g.: 60 days from invoice',
      retentionPct: 'Retention (%)',
      discountPct: 'Overall discount (%)',
      discountHelp: 'On what it offers, not on the lines that get filled.',
      discountShort: '{pct} % off',
      totalDeclared: 'Total written by the bidder',
      totalDeclaredHelp: 'The one on its offer. If it does not match its prices, you are told.',
      declaredMismatch: 'Its offer says {declared} and its prices add up to {computed}: check what was mistyped.',
      strongCount: '{count} price is far from the average: check unit and quantity. | {count} prices are far from the average: check units and quantities.',
      exclusions: 'Not included',
      exclusionsHelp: 'What the offer leaves out. One per line, with Enter.',
      exclusionsPlaceholder: 'E.g.: paving reinstatement',
      notes: 'Notes',
      documents: 'No papers | 1 paper | {count} papers',
      documentsTitle: 'Papers of the offer from {supplier}',
      withdrawOffer: 'Withdraw',
      restoreOffer: 'Count it again',
      removeOffer: 'Remove',
      withdrawHelp: 'The bidder backs out. The offer stays in the comparison but stops competing and marking best prices.',
      restoreHelp: 'The offer competes with the others again.',
      removeOfferTitle: 'Remove the offer?',
      removeOfferHelp: 'The offer from {supplier} is deleted with its prices and papers. If the bidder backed out, «Withdraw» is better: that way it is on record.',
      award: 'Award',
      awardTitle: 'Award the package',
      awardLead: 'Award to {supplier} for {amount}.',
      awardSubcontractHelp: 'A subcontract linked to this package is created, with the offer amount, {retention} % retention and {days} days duration. Its scope is read from the package.',
      awardOrderHelp: 'A commitment of {amount} is recorded on the package, and purchasing leaves the order as a draft with these lines. Every order confirmed to this bidder on this package is deducted from the commitment.',
      awardIncomplete: 'This offer leaves {count} line unpriced, worth {amount} at target: it will have to be contracted separately. | This offer leaves {count} lines unpriced, worth {amount} at target: they will have to be contracted separately.',
      signedLabel: 'Signature',
      signedHelp: 'Leave it signed (commits its amount from today)',
      reasonRequired: 'Reason (required: the recommended one is {supplier})',
      reasonOptional: 'Reason (optional)',
      awarded: 'Package awarded. The comparison is now frozen.',
      tableTitle: 'Comparison table',
      viewLabel: 'View',
      view: {
        table: 'Table',
        lines: 'By line',
        offers: 'By offer',
      },
      line: 'Line',
      unitPrice: 'Price',
      amount: 'Amount',
      pastPrices: 'Prices from other works',
      notOffered: 'Not offered',
      filledWith: 'filled with {amount}',
      lastShort: 'last {price}',
      lastLong: 'Last time it offered {price} ({work}, {date})',
      notOfferedLines: 'Lines not offered',
      targetShort: 'target {price} · {amount}',
      best: 'Best',
      pickOffer: 'Offer',
      historyTitle: 'Comparison history',
      cancelTender: 'Close without award',
      cancelTenderHelp: 'The package goes back to «Open». The prices stay in the history: they were real offers.',
      offerDate: 'Offer date',
      pricesTitle: 'Prices per line',
      pricesHelp: 'Blank means «not offered», and it shows that way in the table. A 0 is a price: the line is included in another.',
      importSheet: 'Read their spreadsheet',
      reading: 'Reading…',
      sheetRead: '{file} · sheet «{sheet}»: {priced} of {lines} lines priced. Check and save.',
      sheetMissing: '{count} line is not on their sheet: it stays unpriced. | {count} lines are not on their sheet: they stay unpriced.',
      sheetQuantity: '{code}: offered {offered} {unit} where {asked} were asked for. Its total is not comparable until that is cleared up.',
      sheetUnmatched: 'Row {row} matches no line: {text}',
      draftTotal: 'Offered: {amount} · no unpriced lines | Offered: {amount} · {count} unpriced line | Offered: {amount} · {count} unpriced lines',
      offerSaved: 'Offer saved',
      history: {
        opened: 'Comparison opened with {count} lines',
        edited: 'Rule or date changed',
        lines_refreshed: 'Lines refreshed from the package ({count})',
        cancelled: 'Closed without award: {comment}',
        offer_added: 'Offer from {supplier}',
        offer_revised: 'Offer from {supplier} revised',
        offer_withdrawn: '{supplier} withdraws its offer {comment}',
        offer_restored: 'The offer from {supplier} competes again',
        offer_removed: 'Offer from {supplier} removed',
        awarded: 'Awarded to {supplier} for {amount} {comment}',
        award_cancelled: 'Award to {supplier} undone: {comment}',
      },
      templateLabels: {
        code: 'Code',
        description: 'Description',
        unit: 'Unit',
        quantity: 'Quantity',
        price: 'Unit price',
        amount: 'Amount',
        total: 'TOTAL',
      },
      pdf: {
        title: 'Bid comparison',
        work: 'Work',
        package: 'Package',
        date: 'Date',
        code: 'Code',
        description: 'Description',
        unit: 'Unit',
        quantity: 'Qty',
        target: 'Target',
        unitPrice: 'Price',
        amount: 'Amount',
        notOffered: 'not offered',
        offered: 'Offered',
        homogenized: 'Homogenized',
        vsTarget: 'Against target',
        missing: 'Lines not offered',
        ruleHighest: 'Lines not offered are filled with the highest price offered by the other bidders (or the target if nobody offered them).',
        ruleTarget: 'Lines not offered are filled at the target price.',
        recommended: 'Recommended',
        awardedTo: 'Awarded to',
        reason: 'Reason',
        best: 'Best price',
        frozen: 'Frozen record of the award',
        validUntil: 'valid until',
        expired: 'expired',
        withdrawn: 'withdrawn',
        exclusions: 'Not included',
        discount: 'Discount',
      },
      rowOpen: 'out to tender, no offers | out to tender, 1 offer | out to tender, {count} offers',
      rowAwarded: 'awarded to {supplier}',
      panelAwarded: 'Awarded to {supplier} for {amount}. The comparison stays as a record.',
      panelOpen: 'Out to tender, no offers yet. | Out to tender, 1 offer. | Out to tender, {count} offers.',
      panelNone: 'Ask several bidders to price what the package claims, and compare them against its target.',
      openComparison: 'Open the comparison',
      start: 'Put out to tender',
      fromTender: 'Awarded from a bid comparison: see the other offers and the reason',
      menu: 'Bid comparisons',
      seeWork: 'Open the job',
      seePurchase: 'Open the draft order',
      seeSupplier: 'Supplier file',
      list: {
        title: 'Bid comparisons',
        subtitle: 'The offers for each work package side by side, and who won them',
        all: 'All',
        status: 'Status',
        work: 'Job',
        package: 'Package',
        offers: 'Offers',
        supplier: 'Awarded to',
        amount: 'Amount',
        created: 'Opened',
        count: 'Comparisons',
        search: 'Search by package, job or company',
        emptyTitle: 'No bid comparisons yet',
        emptyHelp: 'They are opened from a job’s work package, with «Put out to tender».',
      },
      opening: {
        title: 'Opening the comparison',
        working: 'Looking for the package’s comparison…',
        noPackage: 'No package given: a comparison is opened from a job’s work package.',
      },
      supplierPanel: {
        awards: 'Awards',
        offers: 'Bids in comparisons',
      },
      packageType: {
        supply: 'Supply',
        subcontract: 'Subcontract',
        hire: 'Hire',
        service: 'Service',
        own_means: 'Own means',
      },
      ui: {
        more: 'Actions',
        price: 'Price',
        unit: 'Unit',
        concept: 'Concept',
        showAll: 'Show all',
        pager: {
          previous: 'Previous',
          next: 'Next',
          of: 'Page {page} of {pages}',
        },
        addDocument: 'Upload a document',
        company: 'Company',
        companyPlaceholder: 'Find the supplier',
        dropDocument: 'Remove the document',
        dropDocumentConfirm: '"{name}" comes off this contract.',
        noDocuments: 'No paperwork yet. Upload the signed contract, addenda and certificates here.',
        start: 'Start',
        uploading: 'Uploading…',
      },
    },
    priceHistory: {
      title: 'Prices offered before for this unit',
      empty: 'No offers stored for this unit of work yet. Every price entered in a comparison will show up here.',
      min: 'Lowest',
      median: 'Median',
      max: 'Highest',
      lastAwarded: 'Last awarded',
      againstTarget: 'Target price of this item: {target}. Median of what was offered: {median}.',
      awarded: 'Awarded',
      howMatched: 'Matched by price-database code or by a similar description, with the same unit and the same numbers (diameter, thickness, strength).',
    },
    tenderPrices: {
      title: 'Quoted prices',
      subtitle: 'What companies have bid and what was contracted, across every work',
      count: 'Prices',
      search: 'Description or code…',
      unitPlaceholder: 'm3, m2, kg…',
      supplier: 'Company',
      which: 'Which prices',
      allPrices: 'Bid and contracted',
      onlyAwarded: 'Contracted only',
      mixedUnits: 'These prices are in several units. Type a unit to see the average, the lowest and the latest price.',
      mean: 'Average price',
      min: 'Lowest',
      max: 'Highest',
      last: 'Latest',
      lastAwarded: 'Latest contracted',
      fromWhere: '{supplier} · {work} · {date}',
      awarded: 'Contracted',
      origin: 'Work and comparison',
      date: 'Date',
      emptyTitle: 'No prices with these filters',
      emptyHelp: 'Every bid entered in a comparison leaves its prices here, line by line.',
      onlySupplier: 'Only the prices of {name}',
      oneSupplier: 'this supplier',
      menu: 'Quoted prices',
    },
    related: {
      tenders: 'Bid comparisons',
      itemPrices: 'Previous prices',
      supplierTenders: 'Bid comparisons',
      purchaseTender: 'Bid comparison',
    },
  },
  fr: {
    // La portada de la ayuda del módulo (help/fr.md).
    help: {
      title: 'Achats',
      subtitle: 'Mettre un lot de chantier en consultation, comparer les offres et attribuer',
    },
    tenders: {
      title: 'Comparatif des offres',
      back: 'Aux lots',
      pageTitle: 'Comparatif · {code} {name}',
      pageSubtitle: 'Comparatif n° {number} · {count} ligne | Comparatif n° {number} · {count} lignes',
      print: 'Imprimer le PDF',
      template: 'Modèle pour les entreprises',
      addOffer: 'Ajouter une offre',
      editOffer: 'Modifier l’offre',
      status: {
        open: 'En consultation',
        awarded: 'Attribué',
        cancelled: 'Clos sans attribution',
      },
      dueOn: 'Offres jusqu’au {date}',
      frozen: 'Figé le {date} : tel qu’on l’a vu à l’attribution',
      target: 'Objectif',
      offersCount: 'Offres',
      bestComplete: 'Meilleure offre complète',
      vsTarget: 'Par rapport à l’objectif',
      linesChanged: 'Le lot a changé depuis l’ouverture du comparatif : ses lignes ne sont plus celles qui ont été demandées.',
      refreshLines: 'Mettre à jour les lignes',
      linesRefreshed: 'Lignes mises à jour. Les prix de chaque entreprise sont conservés par article.',
      ruleLabel: 'Les lignes non chiffrées sont complétées avec',
      rule: {
        highest: 'Le prix le plus élevé proposé',
        target: 'L’objectif',
      },
      ruleHelp: {
        highest: 'Homogénéisé : chaque ligne qu’une entreprise ne chiffre pas est complétée avec le prix le plus élevé proposé par les autres (ou l’objectif si personne ne l’a chiffrée). C’est la règle prudente : cette ligne, vous finirez par la contracter, et elle ne sera pas bon marché.',
        target: 'Homogénéisé : chaque ligne qu’une entreprise ne chiffre pas est complétée au prix objectif de la ligne.',
      },
      awardedTo: 'Attribué à {supplier}',
      awardedBy: 'Par {who} le {date}',
      wasRecommended: 'c’était l’offre recommandée',
      wasNotRecommended: 'la recommandée était {supplier}',
      reason: 'Motif',
      seeSubcontract: 'Voir le contrat de sous-traitance créé',
      orderNote: 'Engagement inscrit sur le lot. Si les achats sont installés, la commande reste en brouillon avec ces lignes et ces prix : c’est l’acheteur qui l’envoie.',
      cancelAward: 'Annuler l’attribution',
      cancelAwardHelp: 'Pour quand l’entreprise se retire avant de commencer. Le comparatif est rouvert avec ses offres, la sous-traitance est annulée (si rien ne lui a été certifié) et l’engagement est levé. L’attribution annulée reste dans l’historique.',
      cancelAwardWithdraw: 'L’entreprise se retire : son offre cesse de concourir',
      awardCancelled: 'Attribution annulée. Le comparatif est de nouveau ouvert.',
      recommended: 'Recommandée : {supplier}',
      recommendedShort: 'Recommandée',
      basis: {
        cheapest_complete: 'C’est l’offre complète et valide la moins chère, lignes homogénéisées comprises.',
        cheapest_complete_expired: 'C’est la complète la moins chère, mais toutes les complètes ont expiré : demandez à l’entreprise de maintenir son prix avant d’attribuer.',
        no_complete: 'Aucune offre n’est complète : c’est la moins chère homogénéisée. Demandez un prix pour ce qui manque avant d’attribuer.',
      },
      belowTarget: 'Elle est {amount} ({pct}) sous l’objectif.',
      aboveTarget: 'Elle est {amount} ({pct}) au-dessus de l’objectif.',
      runnerUp: 'La suivante est {supplier}, {amount} plus chère.',
      cheaperIncomplete: '{supplier} propose {offered}, mais laisse {count} ligne sans prix : homogénéisée, elle revient à {homogenized}. | {supplier} propose {offered}, mais laisse {count} lignes sans prix : homogénéisée, elle revient à {homogenized}.',
      cheaperExpired: '{supplier} serait moins chère ({amount}) mais son offre a expiré : demandez-lui de la renouveler.',
      awardRecommended: 'Attribuer à la recommandée',
      emptyTitle: 'Pas encore d’offres',
      emptyHelp: 'Téléchargez le modèle, envoyez-le à trois ou quatre entreprises et saisissez ici leurs prix : à la main ou en lisant leur tableur.',
      offerStatus: {
        received: 'Reçue',
        awarded: 'Attribuée',
        not_awarded: 'Non retenue',
        withdrawn: 'Retirée',
      },
      expired: 'Expirée',
      missingCount: '{count} ligne sans prix | {count} lignes sans prix',
      offered: 'Proposé',
      homogenized: 'Homogénéisé',
      bestLines: 'Lignes au meilleur prix',
      reference: 'N° d’offre',
      validUntil: 'Valable jusqu’au',
      executionDays: 'Délai (jours)',
      days: '{count} jour | {count} jours',
      paymentTerms: 'Conditions de paiement',
      paymentTermsPlaceholder: 'Ex. : 60 jours date de facture',
      retentionPct: 'Retenue (%)',
      discountPct: 'Remise globale (%)',
      discountHelp: 'Sur ce qu’elle propose, pas sur les lignes complétées.',
      discountShort: 'remise {pct} %',
      totalDeclared: 'Total écrit par l’entreprise',
      totalDeclaredHelp: 'Celui de son offre. S’il ne correspond pas à ses prix, on vous le signale.',
      declaredMismatch: 'Son offre indique {declared} et ses prix totalisent {computed} : vérifiez ce qui a été mal saisi.',
      strongCount: '{count} prix s’écarte fortement de la moyenne : vérifiez unité et quantité. | {count} prix s’écartent fortement de la moyenne : vérifiez unités et quantités.',
      exclusions: 'Non compris',
      exclusionsHelp: 'Ce que l’offre laisse de côté. Une par ligne, avec Entrée.',
      exclusionsPlaceholder: 'Ex. : réfection de chaussée',
      notes: 'Notes',
      documents: 'Aucun document | 1 document | {count} documents',
      documentsTitle: 'Documents de l’offre de {supplier}',
      withdrawOffer: 'Retirer',
      restoreOffer: 'La reprendre en compte',
      removeOffer: 'Supprimer',
      withdrawHelp: 'L’entreprise se retire. L’offre reste dans le comparatif mais ne concourt plus et ne marque plus de meilleurs prix.',
      restoreHelp: 'L’offre concourt de nouveau avec les autres.',
      removeOfferTitle: 'Supprimer l’offre ?',
      removeOfferHelp: 'L’offre de {supplier} est supprimée avec ses prix et ses documents. Si l’entreprise s’est retirée, mieux vaut « Retirer » : ainsi cela reste consigné.',
      award: 'Attribuer',
      awardTitle: 'Attribuer le lot',
      awardLead: 'Attribuer à {supplier} pour {amount}.',
      awardSubcontractHelp: 'Un contrat de sous-traitance lié à ce lot est créé, au montant de l’offre, avec {retention} % de retenue et {days} jours de délai. Son périmètre est lu depuis le lot.',
      awardOrderHelp: 'Un engagement de {amount} est inscrit sur le lot, et les achats laissent la commande en brouillon avec ces lignes. Chaque commande confirmée à cette entreprise sur ce lot vient en déduction de l’engagement.',
      awardIncomplete: 'Cette offre laisse {count} ligne sans prix, d’une valeur de {amount} à l’objectif : il faudra la contracter à part. | Cette offre laisse {count} lignes sans prix, d’une valeur de {amount} à l’objectif : il faudra les contracter à part.',
      signedLabel: 'Signature',
      signedHelp: 'La laisser signée (engage son montant dès aujourd’hui)',
      reasonRequired: 'Motif (obligatoire : la recommandée est {supplier})',
      reasonOptional: 'Motif (facultatif)',
      awarded: 'Lot attribué. Le comparatif est figé.',
      tableTitle: 'Tableau comparatif',
      viewLabel: 'Voir',
      view: {
        table: 'Tableau',
        lines: 'Par ligne',
        offers: 'Par offre',
      },
      line: 'Ligne',
      unitPrice: 'Prix',
      amount: 'Montant',
      pastPrices: 'Prix d’autres chantiers',
      notOffered: 'Non chiffrée',
      filledWith: 'complétée à {amount}',
      lastShort: 'dern. {price}',
      lastLong: 'La dernière fois elle a proposé {price} ({work}, {date})',
      notOfferedLines: 'Lignes non chiffrées',
      targetShort: 'objectif {price} · {amount}',
      best: 'Meilleur',
      pickOffer: 'Offre',
      historyTitle: 'Historique du comparatif',
      cancelTender: 'Clore sans attribuer',
      cancelTenderHelp: 'Le lot repasse à « Ouvert ». Les prix restent dans l’historique : c’étaient de vraies offres.',
      offerDate: 'Date de l’offre',
      pricesTitle: 'Prix par ligne',
      pricesHelp: 'Vide signifie « non chiffrée », et c’est ainsi que cela apparaît dans le tableau. Un 0 est un prix : la ligne est incluse dans une autre.',
      importSheet: 'Lire son tableur',
      reading: 'Lecture…',
      sheetRead: '{file} · feuille « {sheet} » : {priced} lignes chiffrées sur {lines}. Vérifiez et enregistrez.',
      sheetMissing: '{count} ligne n’apparaît pas dans sa feuille : elle reste non chiffrée. | {count} lignes n’apparaissent pas dans sa feuille : elles restent non chiffrées.',
      sheetQuantity: '{code} : elle a chiffré {offered} {unit} là où l’on en demandait {asked}. Son total n’est pas comparable tant que ce n’est pas clarifié.',
      sheetUnmatched: 'Ligne {row} sans correspondance : {text}',
      draftTotal: 'Proposé : {amount} · aucune ligne sans prix | Proposé : {amount} · {count} ligne sans prix | Proposé : {amount} · {count} lignes sans prix',
      offerSaved: 'Offre enregistrée',
      history: {
        opened: 'Comparatif ouvert avec {count} lignes',
        edited: 'Règle ou date modifiée',
        lines_refreshed: 'Lignes mises à jour depuis le lot ({count})',
        cancelled: 'Clos sans attribution : {comment}',
        offer_added: 'Offre de {supplier}',
        offer_revised: 'Offre de {supplier} révisée',
        offer_withdrawn: '{supplier} retire son offre {comment}',
        offer_restored: 'L’offre de {supplier} concourt de nouveau',
        offer_removed: 'Offre de {supplier} supprimée',
        awarded: 'Attribué à {supplier} pour {amount} {comment}',
        award_cancelled: 'Attribution à {supplier} annulée : {comment}',
      },
      templateLabels: {
        code: 'Code',
        description: 'Désignation',
        unit: 'U',
        quantity: 'Quantité',
        price: 'Prix unitaire',
        amount: 'Montant',
        total: 'TOTAL',
      },
      pdf: {
        title: 'Comparatif des offres',
        work: 'Chantier',
        package: 'Lot',
        date: 'Date',
        code: 'Code',
        description: 'Désignation',
        unit: 'U',
        quantity: 'Qté',
        target: 'Objectif',
        unitPrice: 'Prix',
        amount: 'Montant',
        notOffered: 'non chiffrée',
        offered: 'Proposé',
        homogenized: 'Homogénéisé',
        vsTarget: 'Par rapport à l’objectif',
        missing: 'Lignes non chiffrées',
        ruleHighest: 'Les lignes non chiffrées sont complétées avec le prix le plus élevé proposé par les autres entreprises (ou l’objectif si personne ne les a chiffrées).',
        ruleTarget: 'Les lignes non chiffrées sont complétées au prix objectif.',
        recommended: 'Recommandée',
        awardedTo: 'Attribué à',
        reason: 'Motif',
        best: 'Meilleur prix',
        frozen: 'Archive figée de l’attribution',
        validUntil: 'valable jusqu’au',
        expired: 'expirée',
        withdrawn: 'retirée',
        exclusions: 'Non compris',
        discount: 'Remise',
      },
      rowOpen: 'en consultation, sans offre | en consultation, 1 offre | en consultation, {count} offres',
      rowAwarded: 'attribué à {supplier}',
      panelAwarded: 'Attribué à {supplier} pour {amount}. Le comparatif reste comme archive.',
      panelOpen: 'En consultation, encore sans offre. | En consultation, 1 offre. | En consultation, {count} offres.',
      panelNone: 'Demandez un prix à plusieurs entreprises pour ce que réclame le lot, et comparez-les à son objectif.',
      openComparison: 'Ouvrir le comparatif',
      start: 'Mettre en consultation',
      fromTender: 'Attribuée depuis un comparatif d’offres : voir les autres offres et le motif',
      menu: 'Comparatifs',
      seeWork: 'Voir le chantier',
      seePurchase: 'Voir la commande brouillon',
      seeSupplier: 'Fiche du fournisseur',
      list: {
        title: 'Comparatifs d’offres',
        subtitle: 'Les offres de chaque lot de chantier côte à côte, et à qui il a été attribué',
        all: 'Tous',
        status: 'Statut',
        work: 'Chantier',
        package: 'Lot',
        offers: 'Offres',
        supplier: 'Attribué à',
        amount: 'Montant',
        created: 'Ouvert',
        count: 'Comparatifs',
        search: 'Rechercher par lot, chantier ou entreprise',
        emptyTitle: 'Aucun comparatif',
        emptyHelp: 'Ils s’ouvrent depuis le lot de consultation d’un chantier, avec « Mettre en concurrence ».',
      },
      opening: {
        title: 'Ouverture du comparatif',
        working: 'Recherche du comparatif du lot…',
        noPackage: 'Lot manquant : un comparatif s’ouvre depuis le lot de consultation d’un chantier.',
      },
      supplierPanel: {
        awards: 'Attributions',
        offers: 'Offres dans les comparatifs',
      },
      packageType: {
        supply: 'Fourniture',
        subcontract: 'Sous-traitance',
        hire: 'Location',
        service: 'Service',
        own_means: 'Moyens propres',
      },
      ui: {
        more: 'Actions',
        price: 'Prix',
        unit: 'U',
        concept: 'Concept',
        showAll: 'Tout afficher',
        pager: {
          previous: 'Précédent',
          next: 'Suivant',
          of: 'Page {page} sur {pages}',
        },
        addDocument: 'Téléverser un document',
        company: 'Entreprise',
        companyPlaceholder: 'Chercher le fournisseur',
        dropDocument: 'Retirer le document',
        dropDocumentConfirm: '« {name} » quitte ce contrat.',
        noDocuments: 'Aucune pièce. Déposez ici le contrat signé, les avenants et les attestations.',
        start: 'Début',
        uploading: 'Envoi…',
      },
    },
    priceHistory: {
      title: 'Prix proposés auparavant pour cette unité',
      empty: 'Aucune offre enregistrée pour cette unité d’ouvrage pour l’instant. Chaque prix saisi dans un comparatif apparaîtra ici.',
      min: 'Minimum',
      median: 'Médiane',
      max: 'Maximum',
      lastAwarded: 'Dernier attribué',
      againstTarget: 'Prix objectif de cet article : {target}. Médiane des prix proposés : {median}.',
      awarded: 'Attribué',
      howMatched: 'Recherche par code de base de prix ou par désignation proche, avec la même unité et les mêmes chiffres (diamètre, épaisseur, résistance).',
    },
    tenderPrices: {
      title: 'Prix proposés',
      subtitle: 'Ce que les entreprises ont offert et ce qui a été contracté, sur tous les chantiers',
      count: 'Prix',
      search: 'Désignation ou code…',
      unitPlaceholder: 'm3, m2, kg…',
      supplier: 'Entreprise',
      which: 'Quels prix',
      allPrices: 'Offerts et contractés',
      onlyAwarded: 'Contractés seulement',
      mixedUnits: 'Ces prix sont dans plusieurs unités. Saisissez une unité pour voir le prix moyen, le plus bas et le dernier.',
      mean: 'Prix moyen',
      min: 'Plus bas',
      max: 'Plus haut',
      last: 'Dernier',
      lastAwarded: 'Dernier contracté',
      fromWhere: '{supplier} · {work} · {date}',
      awarded: 'Contracté',
      origin: 'Chantier et comparatif',
      date: 'Date',
      emptyTitle: 'Aucun prix avec ces filtres',
      emptyHelp: 'Chaque offre saisie dans un comparatif laisse ici ses prix, ligne par ligne.',
      onlySupplier: 'Seulement les prix de {name}',
      oneSupplier: 'ce fournisseur',
      menu: 'Prix proposés',
    },
    related: {
      tenders: 'Comparatifs',
      itemPrices: 'Prix antérieurs',
      supplierTenders: 'Comparatifs',
      purchaseTender: 'Comparatif',
    },
  },
}

/**
 * Fusiona estos textos en el catálogo del módulo, idioma a idioma.
 *
 * En profundidad sobre `purchasing` y `permissions`: `related` ya existe en
 * el catálogo grande, y una fusión plana se llevaría sus claves por delante.
 */
export function withTenders(base) {
  const merged = {}
  for (const [locale, catalogue] of Object.entries(base)) {
    const own = tenders[locale] ?? {}
    const mine = catalogue.purchasing ?? {}
    merged[locale] = {
      ...catalogue,
      purchasing: {
        ...mine,
        tenders: { ...(mine.tenders ?? {}), ...own.tenders },
        priceHistory: { ...(mine.priceHistory ?? {}), ...own.priceHistory },
        tenderPrices: { ...(mine.tenderPrices ?? {}), ...own.tenderPrices },
        related: { ...(mine.related ?? {}), ...own.related },
        help: { ...(mine.help ?? {}), ...own.help },
      },
    }
  }
  return merged
}

export default tenders
